import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AuthState, LoginCredentials } from '@/types/auth';
import { authService } from '@/services/authService';

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true, // Start with loading true to prevent premature redirects
  error: null,
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async () => {
    try {
      await authService.logout();
    } catch (error: any) {
      // Don't reject on logout errors, just log them
      console.warn('Logout error:', error);
    }
  }
);

export const validateToken = createAsyncThunk(
  'auth/validateToken',
  async (_, { rejectWithValue }) => {
    try {
      const user = await authService.validateToken();
      if (!user) {
        throw new Error('Token validation failed');
      }
      return user;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Token validation failed');
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.refreshToken();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Token refresh failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      authService.clearTokens();
    },
    initializeAuth: (state) => {
      // Only initialize basic state, don't set as authenticated
      // Let validateToken handle actual authentication verification
      const token = authService.getToken();
      
      
      if (token) {
        state.token = token;
        state.refreshToken = authService.getRefreshToken();
        
        // Try to get user info from token for immediate UI feedback
        const currentUser = authService.getCurrentUser();
        
        if (currentUser) {
          state.user = currentUser;
          // Temporarily set as authenticated for better UX
          // validateToken will confirm or override this
          state.isAuthenticated = true;
        } else {
        }
        
        // Keep isLoading = true since we still need backend validation
        state.isLoading = true;
      } else {
        // No token, definitely not authenticated
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
      }
      
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      });

    // Logout
    builder
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.error = null;
      });

    // Validate Token
    builder
      .addCase(validateToken.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(validateToken.fulfilled, (state, action) => {
        state.isLoading = false;
        
        // Preserve roles from local token if backend response doesn't have them
        const backendUser = action.payload;
        const currentUser = state.user;
        const localTokenUser = authService.getCurrentUser();
        
        
        // Merge user data, prioritizing backend data but preserving roles/permissions from token
        state.user = {
          ...backendUser,
          // Ensure roles and permissions are preserved from token if backend doesn't provide them
          roles: backendUser.roles && backendUser.roles.length > 0 
            ? backendUser.roles 
            : (currentUser?.roles || localTokenUser?.roles || []),
          permissions: backendUser.permissions && backendUser.permissions.length > 0 
            ? backendUser.permissions 
            : (currentUser?.permissions || localTokenUser?.permissions || [])
        };
        
        state.isAuthenticated = true;
        state.token = authService.getToken();
        state.refreshToken = authService.getRefreshToken();
        
      })
      .addCase(validateToken.rejected, (state, action) => {
        state.isLoading = false;
        
        // Only clear auth if the token is actually invalid (not network errors)
        const currentToken = authService.getToken();
        const errorMessage = action.payload as string;
        
        // If we have a token but validation failed due to network issues, keep the current state
        if (currentToken && (errorMessage.includes('Network Error') || errorMessage.includes('timeout'))) {
          // Try to extract user data from token as fallback
          const currentUser = authService.getCurrentUser();
          if (currentUser) {
            state.user = currentUser;
            state.token = currentToken;
            state.refreshToken = authService.getRefreshToken();
            state.isAuthenticated = true;
          }
        } else if (!currentToken || errorMessage.includes('Token validation failed')) {
          // Clear auth state only for actual auth failures or missing tokens
          state.user = null;
          state.token = null;
          state.refreshToken = null;
          state.isAuthenticated = false;
        } else {
          // For other errors, try to maintain state with local token data
          const currentUser = authService.getCurrentUser();
          if (currentUser && currentToken) {
            state.user = currentUser;
            state.token = currentToken;
            state.refreshToken = authService.getRefreshToken();
            state.isAuthenticated = true;
          } else {
            // No valid token data available, clear state
            state.user = null;
            state.token = null;
            state.refreshToken = null;
            state.isAuthenticated = false;
          }
        }
        
        state.error = action.payload as string;
      });

    // Refresh Token
    builder
      .addCase(refreshToken.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload.token;
        if (action.payload.refreshToken) {
          state.refreshToken = action.payload.refreshToken;
        }
      })
      .addCase(refreshToken.rejected, (state) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
      });
  },
});

export const { clearError, setUser, clearAuth, initializeAuth } = authSlice.actions;
export { authSlice };